/**
 * Pipedrive Deal Detail Page
 * Shows deal details with related activities, notes, and files
 */

import { useParams, Link } from 'react-router-dom'
import { usePipedriveDeal } from '@/hooks/use-pipedrive-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Briefcase,
  Building2,
  User,
  Calendar,
  StickyNote,
  FileText,
  ArrowLeft,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
} from 'lucide-react'
import { SEMANTIC, GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'

export default function PipedriveDealDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: deal, isLoading, error } = usePipedriveDeal(id ?? '')

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'won':
        // Pistachio fill + Prussian text
        return { backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }
      case 'lost':
        // Raspberry fill + white text
        return { backgroundColor: SEMANTIC.error.fill, color: SEMANTIC.error.text }
      case 'open':
        // Navy fill + white text
        return { backgroundColor: SEMANTIC.info.fill, color: SEMANTIC.info.text }
      default:
        // Pale grey fill + Navy text
        return { backgroundColor: SEMANTIC.neutral.fill, color: SEMANTIC.neutral.text }
    }
  }

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'won':
        return <CheckCircle className="h-5 w-5" style={{ color: GRACKLE_COLORS.prussianBlue }} />
      case 'lost':
        return <XCircle className="h-5 w-5" style={{ color: GRACKLE_COLORS.darkRaspberry }} />
      default:
        return <Clock className="h-5 w-5" style={{ color: GRACKLE_COLORS.navy }} />
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load deal. Please try again later.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="p-6">
        <div className="bg-muted p-4 rounded-lg">Deal not found.</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/pipedrive/deals">
          <Button variant="ghost" size="icon" aria-label="Back to deals">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Briefcase className="h-6 w-6" aria-hidden="true" />
              {deal.title}
            </h1>
            <Badge variant="secondary" style={getStatusColor(deal.status)}>
              {deal.status || 'Unknown'}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            {deal.stage_name || 'No stage'} {deal.owner ? `• Owner: ${deal.owner}` : ''}
          </p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Deal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Value:</span>
              <span className="font-semibold">
                {deal.value ? `$${deal.value.toLocaleString()}` : '-'}
                {deal.currency && deal.currency !== 'USD' && ` ${deal.currency}`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(deal.status)}
              <span className="text-muted-foreground">Status:</span>
              <span className="font-semibold capitalize">{deal.status || '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Pipeline:</span>
              <span className="font-semibold">{deal.pipeline_name || '-'}</span>
            </div>
            {deal.expected_close_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Expected Close:</span>
                <span className="font-semibold">{deal.expected_close_date}</span>
              </div>
            )}
            {deal.won_time && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" style={{ color: GRACKLE_COLORS.prussianBlue }} />
                <span className="text-muted-foreground">Won:</span>
                <span className="font-semibold" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                  {deal.won_time}
                </span>
              </div>
            )}
            {deal.lost_time && (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4" style={{ color: GRACKLE_COLORS.darkRaspberry }} />
                <span className="text-muted-foreground">Lost:</span>
                <span className="font-semibold" style={{ color: GRACKLE_COLORS.darkRaspberry }}>
                  {deal.lost_time}
                </span>
              </div>
            )}
            {deal.lost_reason && (
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Lost Reason:</span>
                <span className="font-semibold">{deal.lost_reason}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Related Records</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {deal.organization_id ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Organization:</span>
                <Link
                  to={`/pipedrive/organizations/${deal.organization_id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {deal.organization || 'View Organization'}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Organization:</span>
                <span>{deal.organization || '-'}</span>
              </div>
            )}
            {deal.contact_person_id ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Contact:</span>
                <Link
                  to={`/pipedrive/people/${deal.contact_person_id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {deal.contact_person || 'View Contact'}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Contact:</span>
                <span>{deal.contact_person || '-'}</span>
              </div>
            )}
            {deal.industry && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Industry:</span>
                <span className="font-semibold">{deal.industry}</span>
              </div>
            )}
            {deal.region && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Region:</span>
                <span className="font-semibold">{deal.region}</span>
              </div>
            )}
            <div className="pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Activities</p>
                  <p className="text-xl font-bold">{deal.activities_count ?? 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-xl font-bold" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                    {deal.done_activities_count ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Records Tabs */}
      <Tabs defaultValue="activities">
        <TabsList>
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Activities ({deal.activities?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <StickyNote className="h-4 w-4" />
            Notes ({deal.notes?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="files" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Files ({deal.files?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activities">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deal.activities?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No activities found
                      </TableCell>
                    </TableRow>
                  ) : (
                    deal.activities?.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell className="font-medium">
                          <Link
                            to={`/pipedrive/activities/${activity.id}`}
                            className="text-primary hover:underline"
                          >
                            {activity.subject || '-'}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{activity.type || 'Unknown'}</Badge>
                        </TableCell>
                        <TableCell>{activity.assigned_to || '-'}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              color:
                                activity.done === 'Yes'
                                  ? GRACKLE_COLORS.prussianBlue
                                  : CATEGORICAL.clay.solid,
                            }}
                          >
                            {activity.done === 'Yes' ? 'Done' : 'Pending'}
                          </span>
                        </TableCell>
                        <TableCell>{activity.due_date || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Content</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deal.notes?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No notes found
                      </TableCell>
                    </TableRow>
                  ) : (
                    deal.notes?.map((note) => (
                      <TableRow key={note.id}>
                        <TableCell className="max-w-md">
                          <Link
                            to={`/pipedrive/notes/${note.id}`}
                            className="text-primary hover:underline line-clamp-2"
                          >
                            {note.content || '-'}
                          </Link>
                        </TableCell>
                        <TableCell>{note.user_name || '-'}</TableCell>
                        <TableCell>{note.add_time || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="files">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deal.files?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No files found
                      </TableCell>
                    </TableRow>
                  ) : (
                    deal.files?.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            {file.file_name || '-'}
                          </div>
                        </TableCell>
                        <TableCell>{file.file_type || '-'}</TableCell>
                        <TableCell>{file.user_name || '-'}</TableCell>
                        <TableCell>{file.add_time || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Metadata */}
      {(deal.created_date || deal.update_time) && (
        <div className="text-sm text-muted-foreground">
          {deal.created_date && <span>Created: {deal.created_date}</span>}
          {deal.created_date && deal.update_time && <span className="mx-2">•</span>}
          {deal.update_time && <span>Updated: {deal.update_time}</span>}
        </div>
      )}
    </div>
  )
}
